// Actions for the new Twitter-style Direct Messaging system.
// API endpoints: /api/v1/direct_messages/threads/*

import api from '../api';
import type { AppDispatch } from '../store';

// ─── Action type constants ───────────────────────────────────────────────────

export const DM_THREADS_FETCH_REQUEST = 'DM_THREADS_FETCH_REQUEST';
export const DM_THREADS_FETCH_SUCCESS = 'DM_THREADS_FETCH_SUCCESS';
export const DM_THREADS_FETCH_FAIL    = 'DM_THREADS_FETCH_FAIL';

export const DM_MESSAGES_FETCH_REQUEST = 'DM_MESSAGES_FETCH_REQUEST';
export const DM_MESSAGES_FETCH_SUCCESS = 'DM_MESSAGES_FETCH_SUCCESS';
export const DM_MESSAGES_FETCH_FAIL    = 'DM_MESSAGES_FETCH_FAIL';

export const DM_MESSAGE_SEND_REQUEST = 'DM_MESSAGE_SEND_REQUEST';
export const DM_MESSAGE_SEND_SUCCESS = 'DM_MESSAGE_SEND_SUCCESS';
export const DM_MESSAGE_SEND_FAIL    = 'DM_MESSAGE_SEND_FAIL';

export const DM_THREAD_CREATE_REQUEST = 'DM_THREAD_CREATE_REQUEST';
export const DM_THREAD_CREATE_SUCCESS = 'DM_THREAD_CREATE_SUCCESS';
export const DM_THREAD_CREATE_FAIL    = 'DM_THREAD_CREATE_FAIL';

export const DM_MARK_READ_SUCCESS = 'DM_MARK_READ_SUCCESS';

export const DM_REACTION_ADD_SUCCESS    = 'DM_REACTION_ADD_SUCCESS';
export const DM_REACTION_REMOVE_SUCCESS = 'DM_REACTION_REMOVE_SUCCESS';

// WebSocket push events (dispatched from connectDmStream)
export const DM_RECEIVE_MESSAGE    = 'DM_RECEIVE_MESSAGE';
export const DM_DELETE_MESSAGE     = 'DM_DELETE_MESSAGE';
export const DM_UPDATE_REACTION    = 'DM_UPDATE_REACTION';
export const DM_UPDATE_READ_RECEIPT = 'DM_UPDATE_READ_RECEIPT';
export const DM_UPDATE_THREAD     = 'DM_UPDATE_THREAD';
export const DM_DELETE_THREAD     = 'DM_DELETE_THREAD';

export const DM_SET_ACTIVE_THREAD = 'DM_SET_ACTIVE_THREAD';

// ─── Async thunks ────────────────────────────────────────────────────────────

export const fetchDmThreads =
  ({ maxId }: { maxId?: string } = {}) =>
  (dispatch: AppDispatch) => {
    dispatch({ type: DM_THREADS_FETCH_REQUEST });

    return api()
      .get('/api/v1/direct_messages/threads', { params: { max_id: maxId, limit: 20 } })
      .then(({ data, headers }) => {
        dispatch({
          type: DM_THREADS_FETCH_SUCCESS,
          threads: data,
          next: headers.link?.match(/<([^>]+)>;\s*rel="next"/)?.[1] ?? null,
        });
      })
      .catch(() => dispatch({ type: DM_THREADS_FETCH_FAIL }));
  };

export const createDmThread =
  (params: { participantIds: string[]; title?: string; message?: string }) =>
  (dispatch: AppDispatch) => {
    dispatch({ type: DM_THREAD_CREATE_REQUEST });

    return api()
      .post('/api/v1/direct_messages/threads', {
        participant_ids: params.participantIds,
        title:           params.title,
        message:         params.message,
      })
      .then(({ data }) => {
        dispatch({ type: DM_THREAD_CREATE_SUCCESS, thread: data });
        return data;
      })
      .catch(() => dispatch({ type: DM_THREAD_CREATE_FAIL }));
  };

export const fetchDmMessages =
  (threadId: string, { maxId }: { maxId?: string } = {}) =>
  (dispatch: AppDispatch) => {
    dispatch({ type: DM_MESSAGES_FETCH_REQUEST, threadId });

    return api()
      .get(`/api/v1/direct_messages/threads/${threadId}/messages`, {
        params: { max_id: maxId, limit: 40 },
      })
      .then(({ data, headers }) => {
        dispatch({
          type: DM_MESSAGES_FETCH_SUCCESS,
          threadId,
          messages: data,
          next: headers.link?.match(/<([^>]+)>;\s*rel="next"/)?.[1] ?? null,
        });
      })
      .catch(() => dispatch({ type: DM_MESSAGES_FETCH_FAIL, threadId }));
  };

export const sendDmMessage =
  (threadId: string, params: { text: string; replyToId?: string }) =>
  (dispatch: AppDispatch) => {
    dispatch({ type: DM_MESSAGE_SEND_REQUEST, threadId });

    return api()
      .post(`/api/v1/direct_messages/threads/${threadId}/messages`, {
        text:        params.text,
        reply_to_id: params.replyToId,
      })
      .then(({ data }) => {
        dispatch({ type: DM_MESSAGE_SEND_SUCCESS, threadId, message: data });
        return data;
      })
      .catch(() => dispatch({ type: DM_MESSAGE_SEND_FAIL, threadId }));
  };

export const markDmRead =
  (threadId: string, lastReadId: string) =>
  (dispatch: AppDispatch) => {
    return api()
      .post(`/api/v1/direct_messages/threads/${threadId}/read`, {
        last_read_id: lastReadId,
      })
      .then(() => {
        dispatch({ type: DM_MARK_READ_SUCCESS, threadId, lastReadId });
      });
  };

export const addDmReaction =
  (threadId: string, messageId: string, emoji: string) =>
  (_dispatch: AppDispatch) => {
    return api().put(
      `/api/v1/direct_messages/threads/${threadId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
    );
  };

export const removeDmReaction =
  (threadId: string, messageId: string, emoji: string) =>
  (_dispatch: AppDispatch) => {
    return api().delete(
      `/api/v1/direct_messages/threads/${threadId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
    );
  };

// ─── WebSocket push action creators (called from connectDmStream) ────────────

export const receiveDmMessage  = (payload: unknown) => ({ type: DM_RECEIVE_MESSAGE,     payload });
export const deleteDmMessage   = (payload: unknown) => ({ type: DM_DELETE_MESSAGE,      payload });
export const updateDmReaction  = (payload: unknown) => ({ type: DM_UPDATE_REACTION,     payload });
export const updateDmReadReceipt = (payload: unknown) => ({ type: DM_UPDATE_READ_RECEIPT, payload });
export const updateDmThread    = (payload: unknown) => ({ type: DM_UPDATE_THREAD,       payload });
export const deleteDmThread    = (payload: unknown) => ({ type: DM_DELETE_THREAD,       payload });

// ─── UI actions ──────────────────────────────────────────────────────────────

export const setActiveDmThread = (threadId: string | null) => ({
  type: DM_SET_ACTIVE_THREAD,
  threadId,
});
